(() => {
  const toggleSearchEmptyState = () => {
    document.querySelectorAll('.search-modal').forEach((modal) => {
      const input = modal.querySelector('input[type="search"]');
      if (!input) return;

      const sync = () => {
        modal.classList.toggle('samago-search-is-querying', input.value.trim().length > 0);
      };

      input.addEventListener('input', sync);
      input.addEventListener('search', sync);
      sync();
    });
  };

  const syncProductCardGallery = (gallery, activeIndex) => {
    const slides = Array.from(gallery.querySelectorAll('[data-samago-card-slide]'));
    const dots = Array.from(gallery.querySelectorAll('[data-samago-card-dot]'));
    if (slides.length < 2) return;

    const active = (activeIndex + slides.length) % slides.length;
    gallery.dataset.samagoCardReady = 'true';
    gallery.dataset.samagoCardActive = `${active}`;

    slides.forEach((slide, index) => {
      slide.classList.toggle('is-active', index === active);
      slide.setAttribute('aria-hidden', index === active ? 'false' : 'true');
    });
    dots.forEach((dot, index) => dot.classList.toggle('is-active', index === active));
  };

  const moveProductCardGallery = (gallery, step, event) => {
    event?.preventDefault();
    event?.stopPropagation();

    const slides = Array.from(gallery.querySelectorAll('[data-samago-card-slide]'));
    if (slides.length < 2) return;

    let active = Number.parseInt(gallery.dataset.samagoCardActive, 10);
    if (Number.isNaN(active)) {
      active = slides.findIndex((slide) => slide.classList.contains('is-active'));
    }
    if (active < 0) active = 0;

    syncProductCardGallery(gallery, active + step);
  };

  const initProductCardGalleries = (root = document) => {
    root.querySelectorAll('[data-samago-card-gallery]:not([data-samago-card-ready])').forEach((gallery) => {
      const slides = Array.from(gallery.querySelectorAll('[data-samago-card-slide]'));
      if (slides.length < 2) return;

      const active = slides.findIndex((slide) => slide.classList.contains('is-active'));
      syncProductCardGallery(gallery, active < 0 ? 0 : active);
    });
  };

  const bindProductCardGalleryControls = () => {
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const control = target?.closest('[data-samago-card-prev], [data-samago-card-next]');
        if (!control) return;

        const gallery = control.closest('[data-samago-card-gallery]');
        if (!gallery) return;

        moveProductCardGallery(gallery, control.matches('[data-samago-card-prev]') ? -1 : 1, event);
      },
      true
    );
  };

  const bindProductCardGallerySwipe = () => {
    const pointerSwipeStateById = new Map();
    const touchSwipeStateById = new Map();
    const supportsTouchEvents = 'TouchEvent' in window || 'ontouchstart' in window;
    const swipeThreshold = 34;
    const verticalTolerance = 1.25;

    const getSwipeGalleryFromTarget = (target) => {
      if (!(target instanceof Element)) return null;
      const gallery = target?.closest('[data-samago-card-gallery]');
      if (!gallery) return null;

      const slides = gallery.querySelectorAll('[data-samago-card-slide]');
      return slides.length > 1 ? gallery : null;
    };

    const isMobileSwipePointer = (event) => {
      if (event.pointerType === 'touch') return !supportsTouchEvents;
      if (event.pointerType === 'pen') return true;
      return window.matchMedia?.('(max-width: 749px)').matches === true;
    };

    const startSwipe = (swipeStateById, swipeId, gallery, clientX, clientY) => {
      swipeStateById.set(swipeId, {
        gallery,
        startX: clientX,
        startY: clientY,
        isHorizontal: false,
      });
    };

    const trackSwipe = (swipeStateById, swipeId, clientX, clientY, event) => {
      const state = swipeStateById.get(swipeId);
      if (!state) return;

      const horizontalDistance = clientX - state.startX;
      const verticalDistance = clientY - state.startY;
      state.isHorizontal =
        Math.abs(horizontalDistance) >= swipeThreshold &&
        Math.abs(horizontalDistance) > Math.abs(verticalDistance) * verticalTolerance;

      if (state.isHorizontal) {
        event.preventDefault();
      }
    };

    const finishSwipe = (swipeStateById, swipeId, clientX, clientY, event) => {
      const state = swipeStateById.get(swipeId);
      if (!state) return;
      swipeStateById.delete(swipeId);

      const horizontalDistance = clientX - state.startX;
      const verticalDistance = clientY - state.startY;
      const isHorizontalSwipe =
        state.isHorizontal ||
        (Math.abs(horizontalDistance) >= swipeThreshold &&
          Math.abs(horizontalDistance) > Math.abs(verticalDistance) * verticalTolerance);

      if (!isHorizontalSwipe) return;

      const gallery = state.gallery;
      gallery.setAttribute('data-samago-swipe-suppress-click', 'true');
      moveProductCardGallery(gallery, horizontalDistance < 0 ? 1 : -1, event);
      window.setTimeout(() => gallery.removeAttribute('data-samago-swipe-suppress-click'), 360);
    };

    document.addEventListener(
      'pointerdown',
      (event) => {
        if (!isMobileSwipePointer(event)) return;

        const gallery = getSwipeGalleryFromTarget(event.target);
        if (!gallery) return;

        startSwipe(pointerSwipeStateById, event.pointerId, gallery, event.clientX, event.clientY);
      },
      true
    );

    document.addEventListener(
      'pointermove',
      (event) => {
        const state = pointerSwipeStateById.get(event.pointerId);
        if (!state) return;

        trackSwipe(pointerSwipeStateById, event.pointerId, event.clientX, event.clientY, event);
      },
      { capture: true, passive: false }
    );

    document.addEventListener(
      'pointerup',
      (event) => {
        const state = pointerSwipeStateById.get(event.pointerId);
        if (!state) return;

        finishSwipe(pointerSwipeStateById, event.pointerId, event.clientX, event.clientY, event);
      },
      { capture: true, passive: false }
    );

    document.addEventListener(
      'pointercancel',
      (event) => {
        pointerSwipeStateById.delete(event.pointerId);
      },
      true
    );

    document.addEventListener(
      'touchstart',
      (event) => {
        if (
          window.matchMedia?.('(max-width: 749px)').matches !== true ||
          event.changedTouches.length !== 1
        )
          return;

        const touch = event.changedTouches[0];
        const gallery = getSwipeGalleryFromTarget(touch.target);
        if (!gallery) return;

        const touchId = `touch-${touch.identifier}`;
        startSwipe(touchSwipeStateById, touchId, gallery, touch.clientX, touch.clientY);
      },
      { capture: true, passive: true }
    );

    document.addEventListener(
      'touchend',
      (event) => {
        if (
          window.matchMedia?.('(max-width: 749px)').matches !== true ||
          event.changedTouches.length !== 1
        )
          return;

        const touch = event.changedTouches[0];
        const touchId = `touch-${touch.identifier}`;
        const state = touchSwipeStateById.get(touchId);
        if (!state) return;

        finishSwipe(touchSwipeStateById, touchId, touch.clientX, touch.clientY, event);
      },
      { capture: true, passive: false }
    );

    document.addEventListener(
      'touchmove',
      (event) => {
        if (
          window.matchMedia?.('(max-width: 749px)').matches !== true ||
          event.changedTouches.length !== 1
        )
          return;

        const touch = event.changedTouches[0];
        const touchId = `touch-${touch.identifier}`;
        const state = touchSwipeStateById.get(touchId);
        if (!state) return;

        trackSwipe(touchSwipeStateById, touchId, touch.clientX, touch.clientY, event);
      },
      { capture: true, passive: false }
    );

    document.addEventListener(
      'touchcancel',
      (event) => {
        const touch = event.changedTouches[0];
        if (!touch) return;

        touchSwipeStateById.delete(`touch-${touch.identifier}`);
      },
      { capture: true, passive: true }
    );

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const gallery = target?.closest('[data-samago-card-gallery]');
        if (gallery?.hasAttribute('data-samago-swipe-suppress-click')) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );
  };

  document.addEventListener('DOMContentLoaded', () => {
    toggleSearchEmptyState();
    initProductCardGalleries();
    bindProductCardGalleryControls();
    bindProductCardGallerySwipe();
  });
})();
